import { cloneElement, ReactNode } from 'react'
import { UseFormReturn } from 'react-hook-form'
import type { IntlFormatters } from 'react-intl'
import styled from 'styled-components'

import {
  AccordionCollapse,
  AccordionItem,
  AccordionToggle,
} from '@awazevr/design-system.components.accordion'
import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import { Button } from '@awazevr/design-system.components.button'
import type { FilterProps } from '@awazevr/design-system.components.filter'
import {
  Input,
  InputControl,
  InputValidationMessage,
  Label,
} from '@awazevr/design-system.components.input'
import { Stack } from '@awazevr/design-system.components.stack'
import { Text } from '@awazevr/design-system.components.text'
import { FilterList } from '@awazevr/design-system.compositions.filter-list'
import {
  Dispatch,
  FILTER_BATHROOM_TOGGLE,
  FILTER_BEDROOM_TOGGLE,
  FILTER_GRADE_TOGGLE,
  FILTER_RATING_TOGGLE,
  ONLY_DISCOUNTED_TOGGLE,
} from '@awazevr/mfe-common.contexts.search'
import type { TmsContextType } from '@awazevr/mfe-common.contexts.tms'
import type { AggregationFeatures } from '@awazevr/mfe-common.types-and-mocks.one-search-client'

import type { OptimizelyDecisionsProps } from '@/context/optimizely-decisions-context'
import { isNovasolPoweredBrand } from '@/utils/isNovasolPoweredBrand'

import {
  handleFilterClick,
  handleMappedFilterClick,
  handleMinMaxPriceFilterKeyPress,
  handlePriceFilterSubmit,
} from './FilterActions'
import {
  ClickedFeature,
  PriceFilterFormValues,
  ProcessedFeature,
} from './Filters.types'
import {
  getOrderedFeatures,
  GroupedFilterCategory,
  handleDLEventOnAccordionClick,
  isAccommodationFeature,
  isSiteFeature,
  setFeatureCountsToZero,
} from './Filters.utils'

import styles from './Filters.module.scss'

const PriceSubmitButton = styled(Button)`
  margin-bottom: 0.5rem !important;
`

interface BaseAccordionProps {
  accordionKey: string
  children: ReactNode
  open?: boolean
  title: ReactNode
  tms: TmsContextType
}

const BaseAccordion = ({
  accordionKey,
  children,
  open = false,
  title,
  tms,
}: BaseAccordionProps): JSX.Element => (
  <AccordionItem
    open={open}
    accordionToggleCallback={(isOpen) =>
      handleDLEventOnAccordionClick(tms, isOpen, accordionKey)
    }
  >
    <AccordionToggle>{title}</AccordionToggle>
    <AccordionCollapse>{children}</AccordionCollapse>
  </AccordionItem>
)

interface FilterListAccordionProps {
  accordionKey: string
  ariaLabel: string
  description?: ReactNode
  filterGroup: string
  filters: FilterProps[]
  onFilterClick: (featureDetails: ClickedFeature) => void
  open?: boolean
  selectedFilters: Array<string | number>
  title: ReactNode
  tms: TmsContextType
}

const FilterListAccordion = ({
  accordionKey,
  ariaLabel,
  description,
  filterGroup,
  filters,
  onFilterClick,
  open = false,
  selectedFilters,
  title,
  tms,
}: FilterListAccordionProps): JSX.Element => (
  <BaseAccordion
    accordionKey={accordionKey}
    open={open}
    title={title}
    tms={tms}
  >
    {description}
    <FilterList
      filterGroup={filterGroup}
      filters={filters}
      selectedFilters={selectedFilters}
      aria-label={ariaLabel}
      onFilterClick={onFilterClick}
    />
  </BaseAccordion>
)

interface FeatureAccordionCommonProps {
  accommodationTypesAsFeatures: ProcessedFeature[]
  allFeatures: ProcessedFeature[]
  dispatch: Dispatch
  filterGroupIdSuffix: string
  formatMessage: IntlFormatters['formatMessage']
  optiContext: OptimizelyDecisionsProps
  selectedFilters: string[]
  siteFeatures: AggregationFeatures[]
  tms: TmsContextType
}

interface PopularFeaturesAccordionProps extends FeatureAccordionCommonProps {
  isInAlphabeticalFiltersExp: boolean
  loading: boolean
  popularFeatures: ProcessedFeature[]
  shouldShowZeroFeaturesCount: boolean
}

export const PopularFeaturesAccordion = ({
  accommodationTypesAsFeatures,
  allFeatures,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  isInAlphabeticalFiltersExp,
  loading,
  optiContext,
  popularFeatures,
  selectedFilters,
  shouldShowZeroFeaturesCount,
  siteFeatures,
  tms,
}: PopularFeaturesAccordionProps): JSX.Element | null => {
  if (!(loading || popularFeatures.length > 0)) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="popular-features"
      ariaLabel={formatMessage({
        id: 'filters.popular-features.aria-label',
        defaultMessage: 'Popular features list',
      })}
      filterGroup={`popularFeatures-${filterGroupIdSuffix}`}
      filters={getOrderedFeatures(
        popularFeatures,
        isInAlphabeticalFiltersExp,
        shouldShowZeroFeaturesCount,
      )}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleFilterClick({
          allFeatures,
          appliedFeatures: selectedFilters,
          dispatch,
          clickedFeature: {
            featureGroupId: 'popularFeatures',
            isSiteFeature: isSiteFeature(featureDetails, siteFeatures),
            isAccommodationFeature: isAccommodationFeature(
              featureDetails,
              accommodationTypesAsFeatures,
            ),
            ...featureDetails,
          },
          tms,
          optiContext,
        })
      }}
      open={true}
      selectedFilters={selectedFilters}
      title={formatMessage({
        id: 'filters.popular-features.heading',
        defaultMessage: 'Popular features',
      })}
      tms={tms}
    />
  )
}

interface PopularDestinationsAccordionProps
  extends FeatureAccordionCommonProps {
  brand: Brand
  isInAlphabeticalFiltersExp: boolean
  loading: boolean
  popularDestinations: ProcessedFeature[]
  shouldShowZeroFeaturesCount: boolean
  showPopularDestinationFiltersExp: boolean
}

export const PopularDestinationsAccordion = ({
  allFeatures,
  brand,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  isInAlphabeticalFiltersExp,
  loading,
  optiContext,
  popularDestinations,
  selectedFilters,
  shouldShowZeroFeaturesCount,
  showPopularDestinationFiltersExp,
  siteFeatures,
  tms,
}: PopularDestinationsAccordionProps): JSX.Element | null => {
  if (
    !showPopularDestinationFiltersExp ||
    ((isNovasolPoweredBrand(brand) || !loading) &&
      popularDestinations.length === 0)
  ) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="popular-destinations"
      ariaLabel={formatMessage({
        id: 'filters.popular-destinations-list.aria-label',
        defaultMessage: 'Popular destinations list',
      })}
      filterGroup={`popularDestinations-${filterGroupIdSuffix}`}
      filters={getOrderedFeatures(
        popularDestinations,
        isInAlphabeticalFiltersExp,
        shouldShowZeroFeaturesCount,
      )}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleFilterClick({
          allFeatures,
          appliedFeatures: selectedFilters,
          dispatch,
          clickedFeature: {
            featureGroupId: 'popularDestinations',
            isSiteFeature: isSiteFeature(featureDetails, siteFeatures),
            ...featureDetails,
          },
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedFilters}
      title={formatMessage({
        id: 'filters.popular-destinations',
        defaultMessage: 'Popular destinations',
      })}
      tms={tms}
    />
  )
}

interface GroupedFilterAccordionProps extends FeatureAccordionCommonProps {
  group: GroupedFilterCategory
}

export const GroupedFilterAccordion = ({
  accommodationTypesAsFeatures,
  allFeatures,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  group,
  optiContext,
  selectedFilters,
  siteFeatures,
  tms,
}: GroupedFilterAccordionProps): JSX.Element | null => {
  return (
    <FilterListAccordion
      accordionKey={group.key}
      ariaLabel={formatMessage(
        {
          id: 'filters.grouped-list.aria-label',
          defaultMessage: '{groupName} list',
        },
        { groupName: group.groupName },
      )}
      filterGroup={`${group.key}-${filterGroupIdSuffix}`}
      filters={group.filters}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleFilterClick({
          allFeatures,
          appliedFeatures: selectedFilters,
          dispatch,
          clickedFeature: {
            isAccommodationFeature: isAccommodationFeature(
              featureDetails,
              accommodationTypesAsFeatures,
            ),
            isSiteFeature: isSiteFeature(featureDetails, siteFeatures),
            featureGroupId: 'allFeatures',
            ...featureDetails,
          },
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedFilters}
      title={group.groupName}
      tms={tms}
    />
  )
}

interface AllFeaturesAccordionProps extends FeatureAccordionCommonProps {
  allVisibleFeatures: ProcessedFeature[]
  isInAlphabeticalFiltersExp: boolean
  loading: boolean
  shouldShowZeroFeaturesCount: boolean
}

export const AllFeaturesAccordion = ({
  accommodationTypesAsFeatures,
  allFeatures,
  allVisibleFeatures,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  isInAlphabeticalFiltersExp,
  loading,
  optiContext,
  selectedFilters,
  shouldShowZeroFeaturesCount,
  siteFeatures,
  tms,
}: AllFeaturesAccordionProps): JSX.Element | null => {
  if (!(loading || allVisibleFeatures.length > 0)) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="all-features"
      ariaLabel={formatMessage({
        id: 'filters.all-features.aria-label',
        defaultMessage: 'All features list',
      })}
      filterGroup={`allFeatures-${filterGroupIdSuffix}`}
      filters={getOrderedFeatures(
        allVisibleFeatures,
        isInAlphabeticalFiltersExp,
        shouldShowZeroFeaturesCount,
      )}
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleFilterClick({
          allFeatures,
          appliedFeatures: selectedFilters,
          dispatch,
          clickedFeature: {
            featureGroupId: 'allFeatures',
            isSiteFeature: isSiteFeature(featureDetails, siteFeatures),
            isAccommodationFeature: isAccommodationFeature(
              featureDetails,
              accommodationTypesAsFeatures,
            ),
            ...featureDetails,
          },
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedFilters}
      title={formatMessage({
        id: 'filters.all-features.heading',
        defaultMessage: 'All features',
      })}
      tms={tms}
    />
  )
}

interface MappedFilterAccordionCommonProps {
  dispatch: Dispatch
  filterGroupIdSuffix: string
  formatMessage: IntlFormatters['formatMessage']
  optiContext: OptimizelyDecisionsProps
  shouldShowZeroFeaturesCount: boolean
  tms: TmsContextType
}

interface BedroomsAccordionProps extends MappedFilterAccordionCommonProps {
  bedroomOptions: FilterProps[]
  loading: boolean
  selectedBedrooms: string[]
}

export const BedroomsAccordion = ({
  bedroomOptions,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  loading,
  optiContext,
  selectedBedrooms,
  shouldShowZeroFeaturesCount,
  tms,
}: BedroomsAccordionProps): JSX.Element | null => {
  if (!(loading || bedroomOptions.length > 0)) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="bedrooms"
      ariaLabel={formatMessage({
        id: 'filters.bedrooms.aria-label',
        defaultMessage: 'Bedrooms',
      })}
      filterGroup={`bedrooms-${filterGroupIdSuffix}`}
      filters={
        shouldShowZeroFeaturesCount
          ? setFeatureCountsToZero(bedroomOptions)
          : bedroomOptions
      }
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleMappedFilterClick({
          allFeatures: bedroomOptions,
          appliedFeatures: selectedBedrooms,
          clickedFeature: {
            featureGroupId: 'bedrooms',
            ...featureDetails,
          },
          dispatch,
          dispatchType: FILTER_BEDROOM_TOGGLE,
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedBedrooms}
      title={formatMessage({
        id: 'filters.bedrooms.heading',
        defaultMessage: 'Bedrooms',
      })}
      tms={tms}
    />
  )
}

interface BathroomsAccordionProps extends MappedFilterAccordionCommonProps {
  bathroomOptions: FilterProps[]
  loading: boolean
  selectedBathrooms: string[]
}

export const BathroomsAccordion = ({
  bathroomOptions,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  loading,
  optiContext,
  selectedBathrooms,
  shouldShowZeroFeaturesCount,
  tms,
}: BathroomsAccordionProps): JSX.Element | null => {
  if (!(loading || bathroomOptions.length > 0)) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="bathrooms"
      ariaLabel={formatMessage({
        id: 'filters.bathrooms.aria-label',
        defaultMessage: 'Bathrooms',
      })}
      filterGroup={`bathrooms-${filterGroupIdSuffix}`}
      filters={
        shouldShowZeroFeaturesCount
          ? setFeatureCountsToZero(bathroomOptions)
          : bathroomOptions
      }
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleMappedFilterClick({
          allFeatures: bathroomOptions,
          appliedFeatures: selectedBathrooms,
          clickedFeature: {
            featureGroupId: 'bathrooms',
            ...featureDetails,
          },
          dispatch,
          dispatchType: FILTER_BATHROOM_TOGGLE,
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedBathrooms}
      title={formatMessage({
        id: 'filters.bathrooms.heading',
        defaultMessage: 'Bathrooms',
      })}
      tms={tms}
    />
  )
}

interface StarRatingAccordionProps extends MappedFilterAccordionCommonProps {
  displayGradeOptions: FilterProps[]
  gradeOptions: FilterProps[]
  loading: boolean
  selectedGrades: string[]
}

export const StarRatingAccordion = ({
  dispatch,
  displayGradeOptions,
  filterGroupIdSuffix,
  formatMessage,
  gradeOptions,
  loading,
  optiContext,
  selectedGrades,
  shouldShowZeroFeaturesCount,
  tms,
}: StarRatingAccordionProps): JSX.Element | null => {
  if (!(loading || gradeOptions.length > 0)) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="star-rating"
      ariaLabel={formatMessage({
        id: 'filters.star-rating.heading',
        defaultMessage: 'Star rating',
      })}
      description={
        <Text sizes={100} mb={12}>
          {formatMessage({
            id: 'filters.star-rating.explanation',
            defaultMessage:
              'To indicate quality, the more stars, the more comfort you can expect from the property.',
          })}
        </Text>
      }
      filterGroup={`grades-${filterGroupIdSuffix}`}
      filters={
        shouldShowZeroFeaturesCount
          ? setFeatureCountsToZero(displayGradeOptions)
          : displayGradeOptions
      }
      onFilterClick={({ featureId }) => {
        handleMappedFilterClick({
          allFeatures: gradeOptions,
          appliedFeatures: selectedGrades,
          clickedFeature: {
            featureId,
            featureGroupId: 'starRating',
          },
          dispatch,
          dispatchType: FILTER_GRADE_TOGGLE,
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedGrades}
      title={formatMessage({
        id: 'filters.star-rating.heading',
        defaultMessage: 'Star rating',
      })}
      tms={tms}
    />
  )
}

interface CustomerReviewAccordionProps
  extends MappedFilterAccordionCommonProps {
  loading: boolean
  reviewOptions: FilterProps[]
  selectedRatings: string[]
}

export const CustomerReviewAccordion = ({
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  loading,
  optiContext,
  reviewOptions,
  selectedRatings,
  shouldShowZeroFeaturesCount,
  tms,
}: CustomerReviewAccordionProps): JSX.Element | null => {
  if (!(loading || reviewOptions.length > 0)) {
    return null
  }

  return (
    <FilterListAccordion
      accordionKey="customer-review"
      ariaLabel={formatMessage({
        id: 'filters.customer-review.aria-label',
        defaultMessage: 'Customer review score',
      })}
      filterGroup={`reviews-${filterGroupIdSuffix}`}
      filters={
        shouldShowZeroFeaturesCount
          ? setFeatureCountsToZero(reviewOptions)
          : reviewOptions
      }
      onFilterClick={(featureDetails: ClickedFeature) => {
        handleMappedFilterClick({
          allFeatures: reviewOptions,
          appliedFeatures: selectedRatings,
          clickedFeature: {
            featureGroupId: 'customerReviewScore',
            ...featureDetails,
          },
          dispatch,
          dispatchType: FILTER_RATING_TOGGLE,
          tms,
          optiContext,
        })
      }}
      selectedFilters={selectedRatings}
      title={formatMessage({
        id: 'filters.customer-review.heading',
        defaultMessage: 'Customer review score',
      })}
      tms={tms}
    />
  )
}

interface PriceRangeAccordionProps {
  currencySymbol: string
  dispatch: Dispatch
  formatMessage: IntlFormatters['formatMessage']
  formHooks: UseFormReturn<PriceFilterFormValues>
  priceRangeError: boolean
  setPriceRangeError: React.Dispatch<React.SetStateAction<boolean>>
  showPriceFilter: string
  tms: TmsContextType
}

export const PriceRangeAccordion = ({
  currencySymbol,
  dispatch,
  formatMessage,
  formHooks,
  priceRangeError,
  setPriceRangeError,
  showPriceFilter,
  tms,
}: PriceRangeAccordionProps): JSX.Element | null => {
  if (!showPriceFilter) {
    return null
  }

  return (
    <BaseAccordion
      accordionKey="price-range"
      title={formatMessage(
        {
          id: 'filters.price-range.heading',
          defaultMessage: 'Price Range ({currency})',
        },
        { currency: currencySymbol },
      )}
      tms={tms}
    >
      <Text sizes={100} mb={12}>
        {formatMessage({
          id: 'filters.price-range.explanation',
          defaultMessage:
            'Enter the minimum and maximum price for the total duration of your stay',
        })}
      </Text>
      <form
        onSubmit={formHooks.handleSubmit((data) =>
          handlePriceFilterSubmit(data, setPriceRangeError, dispatch, tms),
        )}
        noValidate
      >
        <Stack isHorizontal align="flex-end">
          <InputControl>
            <Label htmlFor="minPrice">
              {formatMessage({
                id: 'filters.price-range.min',
                defaultMessage: 'Min',
              })}
            </Label>
            <Input
              name="minPrice"
              aria-label={formatMessage({
                id: 'filters.price-range.aria-min',
                defaultMessage: 'Minimum Price',
              })}
              type="number"
              formHooks={formHooks}
              onKeyDown={handleMinMaxPriceFilterKeyPress}
              status={priceRangeError ? 'error' : 'default'}
            />
          </InputControl>
          <InputControl>
            <Label htmlFor="maxPrice">
              {formatMessage({
                id: 'filters.price-range.max',
                defaultMessage: 'Max',
              })}
            </Label>
            <Input
              name="maxPrice"
              aria-label={formatMessage({
                id: 'filters.price-range.aria-max',
                defaultMessage: 'Maximum Price',
              })}
              type="number"
              formHooks={formHooks}
              onKeyDown={handleMinMaxPriceFilterKeyPress}
              status={priceRangeError ? 'error' : 'default'}
            />
          </InputControl>
          <PriceSubmitButton
            text={formatMessage({
              id: 'filters.price-range.submit',
              defaultMessage: 'Apply',
            })}
            type="submit"
          />
        </Stack>
        {priceRangeError && (
          <InputValidationMessage status="error">
            <Text sizes={100}>
              {formatMessage({
                id: 'filters.price-range.validation-message',
                defaultMessage: "Min value can't be greater than the Max value",
              })}
            </Text>
          </InputValidationMessage>
        )}
      </form>
    </BaseAccordion>
  )
}

interface ExtrasAccordionProps {
  discountsSelected: boolean
  dispatch: Dispatch
  filterGroupIdSuffix: string
  formatMessage: IntlFormatters['formatMessage']
  showExtras: string
  tms: TmsContextType
}

export const ExtrasAccordion = ({
  discountsSelected,
  dispatch,
  filterGroupIdSuffix,
  formatMessage,
  showExtras,
  tms,
}: ExtrasAccordionProps): JSX.Element | null => {
  if (!showExtras) {
    return null
  }

  return (
    <BaseAccordion
      accordionKey="extras"
      title={formatMessage({
        id: 'filters.extras.heading',
        defaultMessage: 'Extras',
      })}
      tms={tms}
    >
      <div className={styles.extrasFilter}>
        <FilterList
          filterGroup={`extras-${filterGroupIdSuffix}`}
          filters={[
            {
              featureId: 1,
              featureName: formatMessage({
                id: 'filters.extras.deals',
                defaultMessage: 'Deals',
              }),
              count: 1,
            },
          ]}
          selectedFilters={discountsSelected ? [1] : []}
          aria-label={formatMessage({
            id: 'filters.extras.aria-label',
            defaultMessage: 'Extras',
          })}
          onFilterClick={() => {
            dispatch({ type: ONLY_DISCOUNTED_TOGGLE })
          }}
        />
      </div>
    </BaseAccordion>
  )
}

export interface AccordionSection {
  element: JSX.Element | null
  key: string
  title: string
}

const pinnedAccordionKeys = ['popularFeatures']

export const getOrderedAccordionSections = (
  sections: AccordionSection[],
  {
    orderedKeys = [],
  }: {
    orderedKeys?: string[]
  } = {},
): Array<JSX.Element> => {
  const orderMap = new Map(orderedKeys.map((key, index) => [key, index]))
  const pinnedOrderMap = new Map(
    pinnedAccordionKeys.map((key, index) => [key, index]),
  )

  return sections
    .map((section, index) => ({ ...section, index }))
    .sort((firstSection, secondSection) => {
      const firstPinnedOrder = pinnedOrderMap.get(firstSection.key)
      const secondPinnedOrder = pinnedOrderMap.get(secondSection.key)

      if (firstPinnedOrder !== undefined || secondPinnedOrder !== undefined) {
        if (firstPinnedOrder === undefined) {
          return 1
        }

        if (secondPinnedOrder === undefined) {
          return -1
        }

        return firstPinnedOrder - secondPinnedOrder
      }

      const firstOrder = orderMap.get(firstSection.key)
      const secondOrder = orderMap.get(secondSection.key)

      if (firstOrder === undefined && secondOrder === undefined) {
        return firstSection.index - secondSection.index
      }

      if (firstOrder === undefined) {
        return 1
      }

      if (secondOrder === undefined) {
        return -1
      }

      return firstOrder - secondOrder
    })
    .flatMap(({ element, key }) =>
      element ? [cloneElement(element, { key })] : [],
    )
}
